import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, Images, Lock, Heart } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { floatEmojis } from "@/lib/celebrate";
import { api, imgSrc, formatErr } from "@/lib/api";
import { UserAvatar } from "@/components/UserBits";

function WantButton({ u, onChange }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (user?.user_id === u.user.user_id) return u.wants > 0 ? <span className="absolute top-2 left-2 px-2.5 h-7 rounded-full bg-white/90 text-rose-600 text-[11px] font-extrabold inline-flex items-center gap-1" data-testid={`unboxing-wants-${u.post_id}`}><Heart className="w-3.5 h-3.5 fill-rose-500" /> {u.wants}</span> : null;
  const tap = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (busy) return;
    const el = e.currentTarget;
    setBusy(true);
    try {
      const { data } = await api.post(`/mystery-box/unboxings/${u.post_id}/want`);
      if (data.wanted) floatEmojis(el, "😍", 6);
      onChange({ ...u, wanted: data.wanted, wants: data.wants });
    } catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };
  return (
    <motion.button onClick={tap} whileTap={{ scale: 0.85 }} data-testid={`unboxing-want-${u.post_id}`} aria-pressed={u.wanted}
      className={`absolute top-2 left-2 px-2.5 h-8 rounded-full text-[11px] font-extrabold inline-flex items-center gap-1 shadow-md transition-colors ${u.wanted ? "bg-rose-500 text-white" : "bg-white/90 text-rose-600 hover:bg-white"}`}>
      <Heart className={`w-3.5 h-3.5 ${u.wanted ? "fill-white" : ""}`} /> Will ich auch!{u.wants > 0 && <span className="opacity-90">· {u.wants}</span>}
    </motion.button>
  );
}

export function UnboxingGallery() {
  const [list, setList] = useState(null);
  const update = (n) => setList((l) => l.map((x) => (x.post_id === n.post_id ? n : x)));
  const [all, setAll] = useState(false);
  useEffect(() => { api.get("/mystery-box/unboxings").then(({ data }) => setList(data)).catch(() => setList([])); }, []);
  if (!list) return null;
  const shown = all ? list : list.slice(0, 6);
  return (
    <section className="relative overflow-hidden rounded-[2rem] vip-lux p-6 sm:p-8 border border-[#c99a3c]/50 space-y-5 fade-up" data-testid="unboxing-gallery">
      <span className="vip-lux-pattern" aria-hidden />
      <div className="relative">
        <p className="text-xs font-extrabold tracking-[0.3em] uppercase text-[#e6c56f] flex items-center gap-2"><Camera className="w-4 h-4" /> Unboxing-Galerie</p>
        <h2 className="font-display text-2xl font-extrabold vip-gold-title">Das steckte in den Mystery-Boxen</h2>
        <p className="text-sm text-[#cdb88f] mt-1">Echte Fotos von Unterstützern, die ihre Box ausgepackt haben.</p>
      </div>
      {list.length === 0 ? (
        <p className="relative text-sm text-[#efdcae]" data-testid="unboxing-empty">Noch keine Unboxing-Fotos – die ersten Boxen sind bald unterwegs. Sei der Erste, der seine Box zeigt!</p>
      ) : (
        <div className="relative grid grid-cols-2 sm:grid-cols-3 gap-3">
          {shown.map((u, i) => (
            <motion.div key={u.post_id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 6) * 0.06 }}>
              <Link to={`/post/${u.post_id}`} data-testid={`unboxing-item-${u.post_id}`} className="group block relative aspect-square rounded-2xl overflow-hidden border border-[#c99a3c]/40">
                <img src={imgSrc(u.image)} alt={`Unboxing von ${u.user.name}`} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <span className="absolute inset-x-0 bottom-0 p-2.5 bg-gradient-to-t from-black/75 to-transparent flex items-center gap-2">
                  <UserAvatar userId={u.user.user_id} name={u.user.name} picture={u.user.picture} size="w-7 h-7" text="text-[10px]" crown={false} />
                  <span className="text-white text-xs font-bold truncate">{u.user.name}</span>
                </span>
                <WantButton u={u} onChange={update} />
                {u.count > 1 && <span className="absolute top-2 right-2 px-2 h-6 rounded-full bg-black/60 text-white text-[11px] font-bold inline-flex items-center gap-1"><Images className="w-3 h-3" /> {u.count}</span>}
              </Link>
            </motion.div>
          ))}
        </div>
      )}
      {list.length > 6 && <button onClick={() => setAll(!all)} data-testid="unboxing-toggle" className="relative text-sm font-bold text-[#f6e3a6] underline">{all ? "Weniger anzeigen" : `Alle ${list.length} Unboxings ansehen`}</button>}
    </section>
  );
}

export function UnboxingTeaser() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/public/unboxing-preview").then(({ data }) => setD(data)).catch(() => setD(null)); }, []);
  if (!d || d.images.length === 0) return null;
  return (
    <section className="relative overflow-hidden rounded-[2rem] vip-lux p-6 sm:p-8 border border-[#c99a3c]/50 space-y-5 fade-up" data-testid="unboxing-teaser">
      <span className="vip-lux-pattern" aria-hidden />
      <div className="relative">
        <p className="text-xs font-extrabold tracking-[0.3em] uppercase text-[#e6c56f] flex items-center gap-2"><Camera className="w-4 h-4" /> Monatliche Mystery-Box</p>
        <h2 className="font-display text-2xl font-extrabold vip-gold-title">Was steckt wohl in der Box?</h2>
        <p className="text-sm text-[#cdb88f] mt-1">Projekt Unterstützer bekommen alle 30 Tage ein echtes Überraschungspaket nach Hause. {d.total} {d.total === 1 ? "Unterstützer hat" : "Unterstützer haben"} die Box schon ausgepackt.</p>
      </div>
      <div className="relative grid grid-cols-3 gap-3">
        {d.images.map((src, i) => (
          <motion.div key={src} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
            className="relative aspect-square rounded-2xl overflow-hidden border border-[#c99a3c]/40" data-testid={`unboxing-teaser-${i}`}>
            <img src={imgSrc(src)} alt="" aria-hidden draggable={false} className="w-full h-full object-cover scale-110 blur-md select-none pointer-events-none" />
            <span className="absolute inset-0 bg-[#2a0f1d]/25 flex items-center justify-center"><span className="w-11 h-11 rounded-full vip-gold-metal flex items-center justify-center"><Lock className="w-5 h-5" /></span></span>
          </motion.div>
        ))}
      </div>
      <Link to="/registrieren" data-testid="unboxing-teaser-cta" className="relative inline-flex h-11 px-6 items-center rounded-full vip-gold-metal font-extrabold text-sm">Mitglied werden & Box freischalten</Link>
    </section>
  );
}
